import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

const WorkforceIdSchema = Type.String({ minLength: 1, maxLength: 512 });
const WorkforceDateTimeSchema = Type.String({ format: "date-time" });

export const WorkforceRoleSchema = Type.Union([
  Type.Literal("MANAGER"),
  Type.Literal("CASHIER"),
  Type.Literal("WAITER"),
  Type.Literal("COACH"),
]);
export type WorkforceRole = Static<typeof WorkforceRoleSchema>;

export const NETWORK_SIMULATION_OPERATOR_ROLES = ["OWNER", "MANAGER", "CASHIER"] as const;
export type NetworkSimulationOperatorRole = (typeof NETWORK_SIMULATION_OPERATOR_ROLES)[number];

export const WorkforceChainSchema = Type.Object(
  {
    tenantId: WorkforceIdSchema,
    id: WorkforceIdSchema,
    name: Type.String({ minLength: 1, maxLength: 120 }),
    status: Type.Union([Type.Literal("ACTIVE"), Type.Literal("DISABLED")]),
    storeIds: Type.Array(WorkforceIdSchema, { uniqueItems: true }),
    version: Type.Integer({ minimum: 1 }),
    createdAt: WorkforceDateTimeSchema,
    updatedAt: WorkforceDateTimeSchema,
  },
  { additionalProperties: false },
);
export type WorkforceChain = Static<typeof WorkforceChainSchema>;

export const WorkforcePersonSchema = Type.Object(
  {
    tenantId: WorkforceIdSchema,
    id: WorkforceIdSchema,
    displayName: Type.String({ minLength: 1, maxLength: 80 }),
    status: Type.Literal("ACTIVE"),
    version: Type.Integer({ minimum: 1 }),
    createdAt: WorkforceDateTimeSchema,
    updatedAt: WorkforceDateTimeSchema,
  },
  { additionalProperties: false },
);
export type WorkforcePerson = Static<typeof WorkforcePersonSchema>;

export const WorkforceProjectionPersonV3Schema = Type.Object(
  {
    ...WorkforcePersonSchema.properties,
    aggregateVersion: Type.Integer({ minimum: 1 }),
  },
  { additionalProperties: false },
);
export type WorkforceProjectionPersonV3 = Static<typeof WorkforceProjectionPersonV3Schema>;

export const WorkforcePersonContactSchema = Type.Object(
  {
    tenantId: WorkforceIdSchema,
    id: WorkforceIdSchema,
    personId: WorkforceIdSchema,
    accountId: Type.Union([WorkforceIdSchema, Type.Null()]),
    contactType: Type.Union([Type.Literal("PHONE"), Type.Literal("WECHAT")]),
    normalizedValue: Type.String({ minLength: 1, maxLength: 160 }),
    verifiedAt: Type.Union([WorkforceDateTimeSchema, Type.Null()]),
    primary: Type.Boolean(),
    loginIdentity: Type.Boolean(),
    createdAt: WorkforceDateTimeSchema,
    updatedAt: WorkforceDateTimeSchema,
  },
  { additionalProperties: false },
);
export type WorkforcePersonContact = Static<typeof WorkforcePersonContactSchema>;

export const WorkforceAccountSchema = Type.Object(
  {
    tenantId: WorkforceIdSchema,
    id: WorkforceIdSchema,
    personId: Type.Union([WorkforceIdSchema, Type.Null()]),
    status: Type.Union([
      Type.Literal("PENDING"),
      Type.Literal("ACTIVE"),
      Type.Literal("DISABLED"),
      Type.Literal("MIGRATION_REVIEW"),
    ]),
    identityVersion: Type.Integer({ minimum: 1 }),
    createdAt: WorkforceDateTimeSchema,
    updatedAt: WorkforceDateTimeSchema,
  },
  { additionalProperties: false },
);
export type WorkforceAccount = Static<typeof WorkforceAccountSchema>;

export const WorkforceEmploymentSchema = Type.Object(
  {
    tenantId: WorkforceIdSchema,
    id: WorkforceIdSchema,
    personId: WorkforceIdSchema,
    storeId: WorkforceIdSchema,
    employmentNumber: Type.String({ minLength: 1, maxLength: 80 }),
    positionTitle: Type.String({ minLength: 1, maxLength: 80 }),
    status: Type.Union([
      Type.Literal("PENDING"),
      Type.Literal("ACTIVE"),
      Type.Literal("REVIEW_REQUIRED"),
      Type.Literal("ENDED"),
    ]),
    roles: Type.Array(WorkforceRoleSchema, { uniqueItems: true }),
    startedAt: WorkforceDateTimeSchema,
    endedAt: Type.Union([WorkforceDateTimeSchema, Type.Null()]),
    version: Type.Integer({ minimum: 1 }),
    createdAt: WorkforceDateTimeSchema,
    updatedAt: WorkforceDateTimeSchema,
  },
  { additionalProperties: false },
);
export type WorkforceEmployment = Static<typeof WorkforceEmploymentSchema>;

export const WorkforceOwnershipSchema = Type.Object(
  {
    tenantId: WorkforceIdSchema,
    id: WorkforceIdSchema,
    personId: WorkforceIdSchema,
    status: Type.Union([
      Type.Literal("ACTIVE"),
      Type.Literal("REVIEW_REQUIRED"),
      Type.Literal("ENDED"),
    ]),
    startedAt: WorkforceDateTimeSchema,
    endedAt: Type.Union([WorkforceDateTimeSchema, Type.Null()]),
    version: Type.Integer({ minimum: 1 }),
    createdAt: WorkforceDateTimeSchema,
    updatedAt: WorkforceDateTimeSchema,
  },
  { additionalProperties: false },
);
export type WorkforceOwnership = Static<typeof WorkforceOwnershipSchema>;

export const WorkforceCoachExtensionSchema = Type.Object(
  {
    tenantId: WorkforceIdSchema,
    storeId: WorkforceIdSchema,
    coachId: WorkforceIdSchema,
    personId: WorkforceIdSchema,
    employmentId: WorkforceIdSchema,
    status: Type.Union([Type.Literal("ACTIVE"), Type.Literal("DISABLED")]),
    version: Type.Integer({ minimum: 1 }),
    createdAt: WorkforceDateTimeSchema,
    updatedAt: WorkforceDateTimeSchema,
  },
  { additionalProperties: false },
);
export type WorkforceCoachExtension = Static<typeof WorkforceCoachExtensionSchema>;

export const WorkforceAccountAliasSchema = Type.Object(
  {
    aliasId: WorkforceIdSchema,
    accountId: WorkforceIdSchema,
  },
  { additionalProperties: false },
);
export type WorkforceAccountAlias = Static<typeof WorkforceAccountAliasSchema>;

const WorkforceActorSnapshotCommon = {
  id: WorkforceIdSchema,
  accountId: WorkforceIdSchema,
  personId: WorkforceIdSchema,
  tenantId: WorkforceIdSchema,
  storeId: WorkforceIdSchema,
  displayName: Type.String({ minLength: 1, maxLength: 80 }),
  identityVersion: Type.Integer({ minimum: 1 }),
  capturedAt: WorkforceDateTimeSchema,
};

export const WorkforceActorSnapshotSchema = Type.Union([
  Type.Object(
    {
      ...WorkforceActorSnapshotCommon,
      employmentId: WorkforceIdSchema,
      ownershipId: Type.Union([WorkforceIdSchema, Type.Null()]),
      roles: Type.Array(WorkforceRoleSchema, { minItems: 1, uniqueItems: true }),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...WorkforceActorSnapshotCommon,
      employmentId: Type.Null(),
      ownershipId: WorkforceIdSchema,
      roles: Type.Array(WorkforceRoleSchema, { maxItems: 0 }),
    },
    { additionalProperties: false },
  ),
]);
export type WorkforceActorSnapshot = Static<typeof WorkforceActorSnapshotSchema>;

const WorkforceSha256Schema = Type.String({ pattern: "^[a-f0-9]{64}$" });

const GatewayLegacyWorkforcePersonSchema = Type.Object(
  {
    id: WorkforceIdSchema,
    displayName: Type.String({ minLength: 1, maxLength: 80 }),
    version: Type.Integer({ minimum: 1 }),
    createdAt: WorkforceDateTimeSchema,
    updatedAt: WorkforceDateTimeSchema,
  },
  { additionalProperties: false },
);

const GatewayLegacyWorkforceAccountSchema = Type.Object(
  {
    id: WorkforceIdSchema,
    identityVersion: Type.Integer({ minimum: 1 }),
    createdAt: WorkforceDateTimeSchema,
    updatedAt: WorkforceDateTimeSchema,
  },
  { additionalProperties: false },
);

const GatewayLegacyWorkforceEmploymentSchema = Type.Object(
  {
    id: WorkforceIdSchema,
    employmentNumber: Type.String({ minLength: 1, maxLength: 80 }),
    positionTitle: Type.String({ minLength: 1, maxLength: 80 }),
    roles: Type.Array(WorkforceRoleSchema, { minItems: 1, uniqueItems: true }),
    startedAt: WorkforceDateTimeSchema,
    version: Type.Integer({ minimum: 1 }),
    createdAt: WorkforceDateTimeSchema,
    updatedAt: WorkforceDateTimeSchema,
  },
  { additionalProperties: false },
);

const GatewayLegacyWorkforceOwnershipSchema = Type.Object(
  {
    id: WorkforceIdSchema,
    startedAt: WorkforceDateTimeSchema,
    version: Type.Integer({ minimum: 1 }),
    createdAt: WorkforceDateTimeSchema,
    updatedAt: WorkforceDateTimeSchema,
  },
  { additionalProperties: false },
);

const GatewayLegacyWorkforceCoachExtensionSchema = Type.Object(
  {
    coachId: WorkforceIdSchema,
    employmentId: WorkforceIdSchema,
    version: Type.Integer({ minimum: 1 }),
    createdAt: WorkforceDateTimeSchema,
    updatedAt: WorkforceDateTimeSchema,
  },
  { additionalProperties: false },
);

export const GatewayLegacyWorkforceIdentitySchema = Type.Object(
  {
    sourceAccountId: WorkforceIdSchema,
    person: GatewayLegacyWorkforcePersonSchema,
    account: GatewayLegacyWorkforceAccountSchema,
    employment: Type.Union([GatewayLegacyWorkforceEmploymentSchema, Type.Null()]),
    ownership: Type.Union([GatewayLegacyWorkforceOwnershipSchema, Type.Null()]),
    coachExtension: Type.Union([GatewayLegacyWorkforceCoachExtensionSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type GatewayLegacyWorkforceIdentity = Static<typeof GatewayLegacyWorkforceIdentitySchema>;

export const GatewayLegacyWorkforceImportRequestSchema = Type.Object(
  {
    schemaVersion: Type.Literal(1),
    gatewayId: WorkforceIdSchema,
    idempotencyKey: Type.String({ minLength: 8, maxLength: 120 }),
    sourceContentSha256: WorkforceSha256Schema,
    identities: Type.Array(GatewayLegacyWorkforceIdentitySchema, {
      minItems: 1,
      maxItems: 100,
    }),
  },
  { additionalProperties: false },
);
export type GatewayLegacyWorkforceImportRequest = Static<
  typeof GatewayLegacyWorkforceImportRequestSchema
>;

export const GatewayLegacyWorkforceImportResultSchema = Type.Object(
  {
    schemaVersion: Type.Literal(1),
    gatewayId: WorkforceIdSchema,
    sourceContentSha256: WorkforceSha256Schema,
    projectionRevision: Type.Integer({ minimum: 1 }),
    importedAccountIds: Type.Array(WorkforceIdSchema, { uniqueItems: true }),
    activatedBootstrapAccountIds: Type.Array(WorkforceIdSchema, { uniqueItems: true }),
    alreadyImportedAccountIds: Type.Array(WorkforceIdSchema, { uniqueItems: true }),
  },
  { additionalProperties: false },
);
export type GatewayLegacyWorkforceImportResult = Static<
  typeof GatewayLegacyWorkforceImportResultSchema
>;

export function isGatewayLegacyWorkforceImportRequest(
  value: unknown,
): value is GatewayLegacyWorkforceImportRequest {
  return Value.Check(GatewayLegacyWorkforceImportRequestSchema, value);
}

export function isGatewayLegacyWorkforceImportResult(
  value: unknown,
): value is GatewayLegacyWorkforceImportResult {
  return Value.Check(GatewayLegacyWorkforceImportResultSchema, value);
}

export const WorkforceAccountDisableFactsSchema = Type.Object(
  {
    personId: WorkforceIdSchema,
    hasOtherStoreOpenEmployment: Type.Boolean(),
    hasLegacyMigrationReviewAccount: Type.Boolean(),
    hasOpenMigrationConflict: Type.Boolean(),
    hasAnyActiveCoachExtension: Type.Boolean(),
  },
  { additionalProperties: false },
);
export type WorkforceAccountDisableFacts = Static<typeof WorkforceAccountDisableFactsSchema>;

const WorkforceProjectionCommonFields = {
  tenantId: WorkforceIdSchema,
  storeId: WorkforceIdSchema,
  revision: Type.Integer({ minimum: 1 }),
  contentSha256: WorkforceSha256Schema,
  generatedAt: WorkforceDateTimeSchema,
  chains: Type.Array(WorkforceChainSchema),
  people: Type.Array(WorkforcePersonSchema),
  accounts: Type.Array(WorkforceAccountSchema),
  accountAliases: Type.Array(WorkforceAccountAliasSchema),
  employments: Type.Array(WorkforceEmploymentSchema),
  ownerships: Type.Array(WorkforceOwnershipSchema),
  coachExtensions: Type.Array(WorkforceCoachExtensionSchema),
};

const WorkforceProjectionV1Schema = Type.Object(
  {
    projectionSchemaVersion: Type.Literal(1),
    ...WorkforceProjectionCommonFields,
  },
  { additionalProperties: false },
);

export const WorkforceProjectionV2Schema = Type.Object(
  {
    projectionSchemaVersion: Type.Literal(2),
    ...WorkforceProjectionCommonFields,
    accountDisableFacts: Type.Array(WorkforceAccountDisableFactsSchema),
  },
  { additionalProperties: false },
);

export const WorkforceProjectionV3Schema = Type.Object(
  {
    projectionSchemaVersion: Type.Literal(3),
    ...WorkforceProjectionCommonFields,
    people: Type.Array(WorkforceProjectionPersonV3Schema),
    accountDisableFacts: Type.Array(WorkforceAccountDisableFactsSchema),
  },
  { additionalProperties: false },
);

export const WorkforceProjectionSchema = Type.Union([
  WorkforceProjectionV1Schema,
  WorkforceProjectionV2Schema,
  WorkforceProjectionV3Schema,
]);
export type WorkforceProjectionV1 = Static<typeof WorkforceProjectionV1Schema> & {
  accountDisableFacts?: never;
};
export type WorkforceProjectionV2 = Static<typeof WorkforceProjectionV2Schema>;
export type WorkforceProjectionV3 = Static<typeof WorkforceProjectionV3Schema>;
export type WorkforceProjection =
  | WorkforceProjectionV1
  | WorkforceProjectionV2
  | WorkforceProjectionV3;

export function isWorkforceProjection(value: unknown): value is WorkforceProjection {
  return Value.Check(WorkforceProjectionSchema, value);
}

export const WorkforceProjectionPullResultSchema = Type.Union([
  Type.Object(
    {
      changed: Type.Literal(false),
      tenantId: WorkforceIdSchema,
      storeId: WorkforceIdSchema,
      revision: Type.Integer({ minimum: 1 }),
      contentSha256: WorkforceSha256Schema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      changed: Type.Literal(true),
      projection: WorkforceProjectionSchema,
    },
    { additionalProperties: false },
  ),
]);
export type WorkforceProjectionPullResult =
  | {
      changed: false;
      tenantId: string;
      storeId: string;
      revision: number;
      contentSha256: string;
    }
  | { changed: true; projection: WorkforceProjection };

export const WorkforcePrincipalAffiliationSchema = Type.Union([
  Type.Object(
    {
      storeId: WorkforceIdSchema,
      relationship: Type.Literal("OWNERSHIP_ONLY"),
      employmentId: Type.Null(),
      ownershipId: WorkforceIdSchema,
      roles: Type.Array(WorkforceRoleSchema, { maxItems: 0 }),
      coachId: Type.Null(),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      storeId: WorkforceIdSchema,
      relationship: Type.Literal("EMPLOYMENT_ONLY"),
      employmentId: WorkforceIdSchema,
      ownershipId: Type.Null(),
      roles: Type.Array(WorkforceRoleSchema, { minItems: 1, uniqueItems: true }),
      coachId: Type.Union([WorkforceIdSchema, Type.Null()]),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      storeId: WorkforceIdSchema,
      relationship: Type.Literal("OWNERSHIP_AND_EMPLOYMENT"),
      employmentId: WorkforceIdSchema,
      ownershipId: WorkforceIdSchema,
      roles: Type.Array(WorkforceRoleSchema, { minItems: 1, uniqueItems: true }),
      coachId: Type.Union([WorkforceIdSchema, Type.Null()]),
    },
    { additionalProperties: false },
  ),
]);
export type WorkforcePrincipalAffiliation = Static<typeof WorkforcePrincipalAffiliationSchema>;

export const WorkforcePrincipalContextSchema = Type.Object(
  {
    tenantId: WorkforceIdSchema,
    account: WorkforceAccountSchema,
    person: Type.Union([WorkforcePersonSchema, Type.Null()]),
    personAggregateVersion: Type.Optional(Type.Integer({ minimum: 1 })),
    affiliations: Type.Array(WorkforcePrincipalAffiliationSchema),
    currentStoreContext: Type.Union([WorkforcePrincipalAffiliationSchema, Type.Null()]),
    resolutionStatus: Type.Union([
      Type.Literal("RESOLVED"),
      Type.Literal("STORE_SELECTION_REQUIRED"),
      Type.Literal("ACCOUNT_INACTIVE"),
      Type.Literal("MIGRATION_REVIEW"),
      Type.Literal("NO_ACTIVE_AFFILIATION"),
    ]),
    projection: Type.Object(
      {
        revision: Type.Integer({ minimum: 1 }),
        contentSha256: WorkforceSha256Schema,
        generatedAt: WorkforceDateTimeSchema,
      },
      { additionalProperties: false },
    ),
  },
  { additionalProperties: false },
);
export type WorkforcePrincipalContext = Static<typeof WorkforcePrincipalContextSchema>;

function workforceContextHasOnlySafeIntegers(value: unknown): boolean {
  if (typeof value === "number") return Number.isSafeInteger(value);
  if (Array.isArray(value)) return value.every(workforceContextHasOnlySafeIntegers);
  if (value !== null && typeof value === "object") {
    return Object.values(value as Record<string, unknown>).every(
      workforceContextHasOnlySafeIntegers,
    );
  }
  return true;
}

function workforceAffiliationsEqual(
  left: WorkforcePrincipalAffiliation,
  right: WorkforcePrincipalAffiliation,
): boolean {
  return (
    left.storeId === right.storeId &&
    left.relationship === right.relationship &&
    left.employmentId === right.employmentId &&
    left.ownershipId === right.ownershipId &&
    left.coachId === right.coachId &&
    left.roles.length === right.roles.length &&
    left.roles.every((role, index) => role === right.roles[index])
  );
}

export function isWorkforcePrincipalContext(
  value: unknown,
  expectedScope?: { tenantId: string; storeId: string },
): value is WorkforcePrincipalContext {
  if (
    !Value.Check(WorkforcePrincipalContextSchema, value) ||
    !workforceContextHasOnlySafeIntegers(value)
  ) {
    return false;
  }
  if (
    value.account.tenantId !== value.tenantId ||
    (value.person !== null &&
      (value.person.tenantId !== value.tenantId || value.account.personId !== value.person.id)) ||
    (value.person !== null && value.account.personId === null) ||
    (expectedScope !== undefined && value.tenantId !== expectedScope.tenantId)
  ) {
    return false;
  }

  const storeIds = new Set<string>();
  for (const affiliation of value.affiliations) {
    if (storeIds.has(affiliation.storeId)) return false;
    storeIds.add(affiliation.storeId);
  }
  const currentStoreContext = value.currentStoreContext;
  if (
    currentStoreContext !== null &&
    (!value.affiliations.some((affiliation) =>
      workforceAffiliationsEqual(affiliation, currentStoreContext),
    ) ||
      (expectedScope !== undefined && currentStoreContext.storeId !== expectedScope.storeId))
  ) {
    return false;
  }

  if (value.resolutionStatus === "MIGRATION_REVIEW") {
    return (
      value.account.status === "MIGRATION_REVIEW" &&
      value.person === null &&
      value.affiliations.length === 0 &&
      value.currentStoreContext === null
    );
  }
  if (value.resolutionStatus === "ACCOUNT_INACTIVE") {
    return (
      (value.account.status === "PENDING" || value.account.status === "DISABLED") &&
      value.person !== null &&
      value.affiliations.length === 0 &&
      value.currentStoreContext === null
    );
  }
  if (value.account.status !== "ACTIVE" || value.person === null) return false;
  if (value.resolutionStatus === "NO_ACTIVE_AFFILIATION") {
    return value.affiliations.length === 0 && value.currentStoreContext === null;
  }
  if (value.resolutionStatus === "STORE_SELECTION_REQUIRED") {
    return value.affiliations.length > 0 && value.currentStoreContext === null;
  }
  return value.affiliations.length > 0 && value.currentStoreContext !== null;
}
